import React from 'react'

export interface RankingWidgetProps {
  items: string[]
  value: string[]
  onChange: (orderedItems: string[]) => void
  disabled?: boolean
}

export function RankingWidget({
  items,
  value,
  onChange,
  disabled = false,
}: RankingWidgetProps): React.ReactElement {
  const orderedItems = value.length > 0 ? value : items

  function moveUp(index: number): void {
    if (index === 0 || disabled) return
    const next = [...orderedItems]
    const prev = next[index - 1] as string
    const curr = next[index] as string
    next[index - 1] = curr
    next[index] = prev
    onChange(next)
  }

  function moveDown(index: number): void {
    if (index === orderedItems.length - 1 || disabled) return
    const next = [...orderedItems]
    const below = next[index + 1] as string
    const curr = next[index] as string
    next[index + 1] = curr
    next[index] = below
    onChange(next)
  }

  return (
    <div
      role="list"
      aria-label="Rank items in order"
      data-testid="ranking-widget"
      className="flex flex-col gap-2 font-sans"
    >
      {orderedItems.map((item, index) => (
        <div
          key={item}
          role="listitem"
          className={[
            'flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium transition-colors',
            'border-neutral-300 bg-neutral-0 text-neutral-700',
            disabled ? 'opacity-50' : '',
          ].join(' ')}
          data-testid={`ranking-item-${index}`}
        >
          <span
            className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold shrink-0"
            aria-label={`Position ${index + 1}`}
          >
            {index + 1}
          </span>

          <span className="flex-1">{item}</span>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              aria-label={`Move ${item} up`}
              disabled={disabled || index === 0}
              onClick={() => moveUp(index)}
              className={[
                'flex items-center justify-center w-8 h-8 rounded-md border transition-colors',
                'focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:ring-offset-1',
                disabled || index === 0
                  ? 'border-neutral-200 text-neutral-300 cursor-not-allowed'
                  : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100 hover:border-neutral-400 cursor-pointer',
              ].join(' ')}
              data-testid={`ranking-move-up-${index}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M7 2.5L3 6.5H5.5V11.5H8.5V6.5H11L7 2.5Z"
                  fill="currentColor"
                />
              </svg>
            </button>

            <button
              type="button"
              aria-label={`Move ${item} down`}
              disabled={disabled || index === orderedItems.length - 1}
              onClick={() => moveDown(index)}
              className={[
                'flex items-center justify-center w-8 h-8 rounded-md border transition-colors',
                'focus:outline-hidden focus:ring-2 focus:ring-brand-500 focus:ring-offset-1',
                disabled || index === orderedItems.length - 1
                  ? 'border-neutral-200 text-neutral-300 cursor-not-allowed'
                  : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100 hover:border-neutral-400 cursor-pointer',
              ].join(' ')}
              data-testid={`ranking-move-down-${index}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M7 11.5L11 7.5H8.5V2.5H5.5V7.5H3L7 11.5Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
